import { getErrorCodeMessage } from './error-message';

/**
 * Normalized API error. Every failure from the http client is converted to
 * this shape so UI and Query error boundaries deal with one type.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(params: {
    message: string;
    status: number;
    code?: string;
    details?: unknown;
  }) {
    super(params.message);
    this.name = 'ApiError';
    this.status = params.status;
    this.code = params.code ?? 'UNKNOWN';
    this.details = params.details;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }
}

/** Type guard for use in error boundaries and catch blocks. */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * Human-readable message for a failed request.
 *
 * Resolution order — see `error-message.ts` for why:
 *
 *   1. the app's translation for `error.code` (the backend's numeric catalog)
 *   2. `fallback`, which the call site has already translated
 *   3. the backend's own `message`
 *
 * The backend message ranks LAST on purpose. It is written for operators and
 * comes back in whatever language that service logs in, so it is a diagnostic
 * of last resort — better than an empty toast, worse than either translated
 * option. It only surfaces for a code this build does not know (a newer
 * backend, or a retired code) when the caller passed an empty fallback.
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (!isApiError(error)) return fallback;

  const translated = getErrorCodeMessage(error.code);
  if (translated !== undefined) return translated;

  if (fallback.trim() !== '') return fallback;
  return error.message.trim() !== '' ? error.message : fallback;
}

/**
 * Field-level validation messages extracted from `ApiError.details`
 * (`body.details` of the error response). Tolerates the common backend shapes:
 *
 *   { field: 'message' }                          — record of strings
 *   { field: ['first message', …] }               — record of string arrays
 *   [{ field: 'x', message: 'y' }, …]             — array of field/message
 *     (also `name`/`path` for the field and `error`/`issue` for the message)
 *
 * The last form with `issue` is what the Eats merchant gateway returns
 * (`ErrorResponse.error.details` in `openapi.json`), e.g.
 * `[{ field: 'id', issue: 'must be a valid UUID' }]`.
 *
 * Anything else — including a missing/foreign `details` — yields `{}` so
 * callers can iterate unconditionally.
 */
export function getApiFieldErrors(error: unknown): Record<string, string> {
  if (!isApiError(error)) return {};
  const details: unknown = error.details;
  const fieldErrors: Record<string, string> = {};

  if (Array.isArray(details)) {
    for (const entry of details as unknown[]) {
      if (entry === null || typeof entry !== 'object') continue;
      const item = entry as Record<string, unknown>;
      const field = firstString(item.field, item.name, item.path);
      const message = firstString(item.message, item.error, item.issue);
      if (field !== undefined && message !== undefined) fieldErrors[field] = message;
    }
    return fieldErrors;
  }

  if (details !== null && typeof details === 'object') {
    for (const [field, value] of Object.entries(details as Record<string, unknown>)) {
      const message = Array.isArray(value)
        ? firstString(...(value as unknown[]))
        : firstString(value);
      if (message !== undefined) fieldErrors[field] = message;
    }
  }
  return fieldErrors;
}

/** First argument that is a non-empty string, if any. */
function firstString(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === 'string' && value.trim() !== '') return value;
  }
  return undefined;
}
